import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, CheckCircle2, ArrowRight, Star, Clock, ShieldCheck, Lock, AlertTriangle, Banknote, FileX, Gavel, Building2, Landmark } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { HeroBackground } from "@/components/site/HeroBackground";
import { stockImages } from "@/data/site/stockImages";
import franklinPhoto from "@/assets/franklin-sofi.webp";
import { PageSEO } from "@/components/site/PageSEO";
import { ClientStoryBlock } from "@/components/site/narrative/ClientStory";
import { brand, founder } from "@/data/site/brand";
import { clientStories } from "@/data/site/clientStories";
import { serviceCategories } from "@/data/site/services";
import ogHome from "@/assets/og/og-home.jpg.asset.json";
import { Reveal, RevealStagger, RevealItem } from "@/components/motion/Reveal";
import { CredentialsLogoBar } from "@/components/site/CredentialsLogoBar";
import { LeadMagnetCTA } from "@/components/site/LeadMagnetCTA";

/** True Mon–Fri 8a–6p Central Time. */
const useIsOpenCT = () => {
  const [isOpen, setIsOpen] = useState(true);
  useEffect(() => {
    const check = () => {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Chicago",
        weekday: "short",
        hour: "numeric",
        hour12: false,
      }).formatToParts(new Date());
      const day = parts.find((p) => p.type === "weekday")?.value ?? "";
      const hour = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
      const weekday = !["Sat", "Sun"].includes(day);
      setIsOpen(weekday && hour >= 8 && hour < 18);
    };
    check();
    const id = setInterval(check, 60_000);
    return () => clearInterval(id);
  }, []);
  return isOpen;
};

// Problem-segmentation cards — symptom first, route to the matching service
const problems = [
  {
    icon: Gavel,
    title: "You got an audit notice",
    blurb: "Discrepancy letter or full examination — we respond, represent, and negotiate so you never face an IRS auditor alone.",
    href: "/services/audit-defense/irs-audit-representation",
    cta: "Get audit representation",
  },
  {
    icon: Banknote,
    title: "You owe more than you can pay",
    blurb: "Balance growing daily with penalties and interest. Offers in Compromise, installment agreements, or hardship status — we find the right path.",
    href: "/services/tax-debt-relief/back-taxes",
    cta: "Settle back taxes",
  },
  {
    icon: Lock,
    title: "They're taking your money",
    blurb: "Wage garnishment. Bank account levied. Refund seized. We can typically stop levies in days — not months.",
    href: "/services/irs-collections-defense/wage-garnishment",
    cta: "Stop garnishments & levies",
  },
  {
    icon: FileX,
    title: "You haven't filed in years",
    blurb: "Two years or ten — the fear is real, prosecution is rare. We get you compliant, protected, and back in good standing.",
    href: "/services/tax-debt-relief/back-taxes",
    cta: "Catch up on filings",
  },
  {
    icon: AlertTriangle,
    title: "You got a CP2000 or Notice of Deficiency",
    blurb: "The IRS says you owe thousands more. You have 90 days to petition Tax Court or lose your right to fight. We represent you.",
    href: "/services/audit-defense/cp2000-response",
    cta: "Respond to the notice",
  },
  {
    icon: Building2,
    title: "Payroll taxes are crushing your business",
    blurb: "941/940 liabilities and Trust Fund Recovery Penalty exposure can shut you down. We resolve it before a Revenue Officer escalates.",
    href: "/services/business-tax/payroll-tax",
    cta: "Resolve payroll tax",
  },
];

const stats = [
  { value: "15+", label: "Years" },
  { value: "$13M+", label: "Resolved" },
  { value: "98%", label: "OIC acceptance" },
  { value: "All 50", label: "States" },
];

const Home = () => {
  const isOpen = useIsOpenCT();
  return (
  <SiteLayout>
    <PageSEO
      title="IRS Tax Resolution | Comprehensive Tax Resolution"
      description="CPA-led IRS tax resolution nationwide. Stop wage garnishments, settle back taxes, defend audits. Free confidential consultation — (214) 646-3802."
      path="/"
      image={ogHome.url}
      jsonLd={[
        {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": `${brand.siteUrl}/#website`,
          name: brand.legalName,
          url: brand.siteUrl,
          potentialAction: {
            "@type": "SearchAction",
            target: `${brand.siteUrl}/blog?q={search_term_string}`,
            "query-input": "required name=search_term_string",
          },
        },
        {
          "@context": "https://schema.org",
          "@type": ["LocalBusiness", "AccountingService"],
          "@id": `${brand.siteUrl}/#localbusiness`,
          name: brand.legalName,
          url: brand.siteUrl,
          telephone: brand.phoneTel,
          email: brand.email,
          image: `${brand.siteUrl}${ogHome.url}`,
          priceRange: "$$",
          address: {
            "@type": "PostalAddress",
            streetAddress: brand.address.street,
            addressLocality: brand.address.city,
            addressRegion: brand.address.region,
            postalCode: brand.address.postalCode,
            addressCountry: brand.address.country,
          },
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              opens: brand.hoursOpen,
              closes: brand.hoursClose,
            },
          ],
          sameAs: [brand.social.facebook, brand.social.linkedin, brand.social.google],
          founder: { "@type": "Person", name: founder.name },
          areaServed: { "@type": "Country", name: "United States" },
        },
      ]}
    />

    {/* 1. Hero — split layout, left tells the story, right card converts the call */}
    <section className="relative bg-gradient-navy text-white overflow-hidden">
      <HeroBackground image={stockImages.taxFormsCalculator} priority />
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-[1.25fr_1fr] gap-10 lg:gap-14 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-gold/15 text-gold border border-gold/30 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider mb-5 rounded">
            <ShieldCheck className="w-4 h-4 flex-shrink-0" />
            CPA firm · 15+ years of IRS representation
          </div>
          <h1 className="font-heading text-[2rem] sm:text-4xl lg:text-6xl font-bold leading-[1.05] mb-5 [text-wrap:balance]">
            CPA-Led IRS Tax Resolution.<br />
            <span className="text-gold">The IRS doesn't forget — neither should you.</span>
          </h1>
          <p className="text-lg text-white/85 mb-6 max-w-xl leading-relaxed">
            A credentialed CPA firm resolving IRS back taxes, wage garnishments, bank levies, and
            audits — nationwide. The longer you wait, the more the IRS takes.
          </p>
          <p className="text-base text-white/80 mb-7 max-w-xl leading-relaxed border-l-2 border-gold/60 pl-4">
            Most cases get easier the day a CPA picks up the phone for you. One call, one honest
            conversation, and you'll know exactly where you stand.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 max-w-xl mb-6">
            <a
              href={`tel:${brand.phoneTel}`}
              className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-navy font-bold px-6 py-4 rounded text-lg shadow-gold flex-1"
            >
              <Phone className="w-5 h-5" /> Call {brand.phone}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-transparent hover:bg-white/10 text-white font-semibold px-6 py-4 rounded text-lg border border-white/25"
            >
              Free Case Review <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Trust bar */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-white/70 text-xs sm:text-sm">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-gold" /> Licensed CPA · All 50 states
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-gold" /> ASTPS Gold · CTRS
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Lock className="w-4 h-4 text-gold" /> Confidential under IRS §7216
            </span>
          </div>
        </div>

        {/* Right — CTA card */}
        <div>
          <div className="bg-white/95 backdrop-blur-sm rounded-lg p-6 lg:p-7 shadow-2xl ring-1 ring-black/5 max-w-md mx-auto lg:ml-auto">
            <div className="flex items-center gap-2 text-urgent font-bold text-sm mb-3">
              <Clock className="w-4 h-4" />
              Only a few free consultations left this week
            </div>
            <h2 className="font-heading text-xl lg:text-2xl font-bold text-navy-dark leading-tight mb-2">
              Talk to a CPA — today.
            </h2>
            <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
              15 minutes. One honest conversation. You'll hang up knowing exactly what the IRS can
              and can't do — and how we'd stop them. Free, confidential, no obligation.
            </p>

            <a
              href={`tel:${brand.phoneTel}`}
              className="flex items-center justify-center gap-2 w-full bg-gold hover:bg-gold-light text-navy-dark font-bold py-4 rounded text-lg shadow-gold"
            >
              <Phone className="w-5 h-5" /> Call {brand.phone}
            </a>
            <p className="text-center text-xs text-muted-foreground mt-2">
              Or <Link to="/contact" className="underline hover:text-navy">request a callback →</Link>
            </p>

            <div className="mt-5 pt-4 border-t border-border flex items-center gap-3">
              <img
                src={franklinPhoto}
                alt={`${founder.name}, ${founder.credentials}`}
                className="w-10 h-10 rounded-full object-cover shrink-0"
                loading="lazy"
               width={40} height={40} />

              <div className="text-xs text-muted-foreground leading-snug">
                <p className="font-semibold text-foreground">{founder.name}, CPA · Founder</p>
                <p>"I answer every call myself — no call center."</p>
              </div>
              <div
                className={`ml-auto shrink-0 px-2 py-0.5 text-[10px] font-bold rounded uppercase tracking-wider ${
                  isOpen ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                }`}
              >
                {isOpen ? "Open now" : "Closed"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* 1b. Stats strip — proof bar just under hero */}
    <section className="bg-navy-dark text-white border-y border-white/10">
      <div className="container mx-auto px-4 py-6">
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6 text-center">
          {stats.map((s) => (
            <div key={s.label}>
              <dt className="sr-only">{s.label}</dt>
              <dd>
                <div className="font-heading text-2xl md:text-3xl font-bold text-gold leading-none">
                  {s.value}
                </div>
                <div className="text-xs md:text-sm text-white/70 mt-1.5">{s.label}</div>
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>

    {/* 1c. Credentials & affiliations logo bar */}
    <CredentialsLogoBar />

    {/* 2. Problem segmentation — symptom-first cards that route users to the right service */}
    <section className="py-16 lg:py-32 bg-gradient-cream">
      <div className="container mx-auto px-4 max-w-6xl">
        <Reveal className="max-w-2xl mb-12 lg:mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-3">
            Tax problems we resolve
          </p>
          <h2 className="font-heading text-3xl lg:text-5xl font-bold text-navy leading-[1.15] [text-wrap:balance] mb-4">
            Does this sound like you?
          </h2>
          <p className="text-lg text-foreground/70 leading-relaxed">
            You're not alone. These are the exact situations we resolve every week — pick the one
            that fits and go straight to the fix.
          </p>
        </Reveal>

        <RevealStagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6" stagger={70}>
          {problems.map((p) => (
            <RevealItem key={p.title + p.href} className="h-full">
              <Link
                to={p.href}
                className="group flex flex-col bg-white rounded-lg border border-border p-6 lg:p-7 shadow-sm hover:shadow-xl hover:-translate-y-0.5 hover:border-gold/40 transition-all h-full"
              >
                <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center mb-4 group-hover:bg-gold transition-colors">
                  <p.icon className="w-6 h-6 text-[hsl(var(--gold))] group-hover:text-navy-dark transition-colors" strokeWidth={2.25} />
                </div>
                <h3 className="font-heading text-lg lg:text-xl font-bold text-navy leading-snug mb-2 [text-wrap:balance]">
                  {p.title}
                </h3>
                <p className="text-foreground/70 leading-relaxed text-[15px] mb-5">{p.blurb}</p>
                <span className="mt-auto text-sm font-semibold text-navy group-hover:text-gold inline-flex items-center gap-1.5">
                  {p.cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            </RevealItem>
          ))}
        </RevealStagger>

        <div className="text-center mt-12 space-y-2">
          <p className="text-foreground/70">
            Not sure which fits?{" "}
            <a href={`tel:${brand.phoneTel}`} className="text-navy font-semibold hover:text-gold">
              Call {brand.phone}
            </a>{" "}
            — we'll figure it out together.
          </p>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 font-semibold text-navy hover:text-gold"
          >
            See all {serviceCategories.reduce((n, c) => n + c.services.length, 0)} services
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>

    {/* 3. One story — editorial, secondary background */}
    <ClientStoryBlock story={clientStories[0]} eyebrow="A real Texas client case" />

    {/* 3b. Lead magnet — 7 Secrets free guide */}
    <LeadMagnetCTA />


    {/* 4. Founder — combined authority + trust, white */}
    <section className="py-16 lg:py-32 bg-gradient-navy-soft border-t-gold-accent">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="max-w-3xl mb-14 lg:mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-3">
            The person who will handle your case
          </p>
          <h2 className="font-heading text-3xl lg:text-5xl font-bold text-navy leading-[1.1] mb-5 [text-wrap:balance]">
            Franklin has done this thousands of times. He hasn't done it for you yet.
          </h2>
          <p className="text-lg text-foreground/70 leading-relaxed">
            Every IRS letter feels like the worst one ever written — until you've
            opened a few thousand of them. Franklin has. He already knows what the
            IRS will ask for, what they'll accept, and the exact move that ends
            this for you. The only thing he doesn't know yet is your name.
          </p>
        </div>
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-16">
          <div>
            <div className="relative mb-6 max-w-[280px]">
              <img
                src={franklinPhoto}
                alt={`${founder.name}, ${founder.credentials} — founder of ${brand.shortName}`}
                width={560}
                height={700}
                loading="lazy"
                className="w-full h-auto rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-3 -right-3 bg-gold text-navy text-xs font-bold uppercase tracking-wider px-3 py-2 rounded shadow-md">
                Founder
              </div>
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-3">
              Meet your specialist
            </p>
            <h3 className="font-heading text-3xl lg:text-4xl font-bold text-navy leading-[1.15] mb-4">
              {founder.name},<br />
              <span className="text-foreground/60 text-2xl lg:text-3xl font-normal">
                {founder.credentials}
              </span>
            </h3>
            <ul className="space-y-2 text-sm text-foreground/75 mt-6">
              {founder.credentialsList.map((c) => (
                <li key={c} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" /> {c}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-foreground/85 leading-[1.75] text-[17px] mb-6">{founder.bio}</p>
            <blockquote className="border-l-2 border-gold pl-5 text-navy italic text-lg leading-relaxed mb-6">
              "Most people who call us are scared. They've been carrying a number around in their
              head for months. My job is to take that number, look at it honestly, and tell them
              what's actually going to happen. No selling."
            </blockquote>
            <Link
              to="/about/franklin-sofi"
              className="inline-flex items-center gap-1 font-semibold text-navy hover:text-gold"
            >
              Read Franklin's full bio <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* 5. Final CTA — risk reversal folded inline */}
    <section className="bg-gradient-navy-gold text-white relative overflow-hidden">
      <div className="container mx-auto px-4 py-14 lg:py-24 max-w-3xl text-center">
        <h2 className="font-heading text-3xl lg:text-5xl font-bold mb-5 leading-[1.15] [text-wrap:balance]">
          The IRS is already working on your case.{" "}
          <span className="text-gold">You should have someone working on it too.</span>
        </h2>
        <p className="text-white/75 mb-10 text-lg max-w-xl mx-auto">
          Penalties accrue daily. Enforcement escalates monthly. The first call is free — and
          almost always changes the math.
        </p>
        <a
          href={`tel:${brand.phoneTel}`}
          className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-navy font-bold px-8 py-4 rounded text-lg"
        >
          <Phone className="w-5 h-5" /> Call {brand.phone}
        </a>
        <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-10 text-sm text-white/70">
          {["Free 15-minute call", "Speak with a CPA", "No sales pitch", "Flat-fee engagements"].map((p) => (
            <li key={p} className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-gold" /> {p}
            </li>
          ))}
        </ul>
      </div>
    </section>
  </SiteLayout>
  );
};

export default Home;
